import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../../styles/FormPages.css";
import DocumentUpload from "../../components/DocumentUpload";
import LocationSelectors from "../../components/LocationSelectors";

const LegalNoticeFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { country, state: stateVal, district, lawCategory } = location.state || {};

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  const [formData, setFormData] = useState({
    country: country || "",
    state: stateVal || "",
    district: district || "",

    senderFullName: "",
    senderFatherOrHusband: "",
    senderAge: "",
    senderGender: "",
    senderOccupation: "",
    senderContact: "",
    senderAddress: "",
    senderEmail: "",
    noticeType: "",
    noticeTypeOther: "",
    recipientName: "",
    recipientDesignation: "",
    recipientOrganization: "",
    recipientAddress: "",
    recipientContact: "",
    recipientEmail: "",
    noticeSubject: "",
    legalBasis: "",
    factsOfCase: "",
    grievanceDetails: "",
    lawsViolated: "",
    demandAction: "",
    timeLimit: "",
    consequences: "",
    evidenceDocuments: "",
    previousCorrespondence: "",
    urgency: "",
    declaration: false,
  });

  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [extractedContent, setExtractedContent] = useState({});

  const handleDocumentsChange = (files, content) => {
    setUploadedDocuments(files);
    setExtractedContent(content);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const selectedCountry = formData.country || country || "";
    const selectedState = formData.state || stateVal || "";
    const selectedDistrict = formData.district || district || "";
    
    let detailsText = `
👤 Sender Details:
Full Name: ${formData.senderFullName}
Father's/Husband's Name: ${formData.senderFatherOrHusband}
Age: ${formData.senderAge}
Gender: ${formData.senderGender}
Occupation: ${formData.senderOccupation}
Contact: ${formData.senderContact}
Email: ${formData.senderEmail}
Address: ${formData.senderAddress}

📧 Notice Details:
Notice Type: ${formData.noticeTypeOther || formData.noticeType}
Subject: ${formData.noticeSubject}
Legal Basis: ${formData.legalBasis}

👥 Recipient Details:
Name: ${formData.recipientName}
Designation: ${formData.recipientDesignation}
Organization: ${formData.recipientOrganization}
Address: ${formData.recipientAddress}
Contact: ${formData.recipientContact}
Email: ${formData.recipientEmail}

⚖️ Legal Content:
Facts of Case: ${formData.factsOfCase}
Grievance Details: ${formData.grievanceDetails}
Laws Violated: ${formData.lawsViolated}
Demanded Action: ${formData.demandAction}
Time Limit: ${formData.timeLimit}
Consequences: ${formData.consequences}

📁 Supporting Information:
Evidence Documents: ${formData.evidenceDocuments}
Previous Correspondence: ${formData.previousCorrespondence}
Urgency: ${formData.urgency}
`.trim();

    // Add extracted document content if available
    if (uploadedDocuments.length > 0) {
      detailsText += `\n\n📄 Supporting Documents:\n`;
      uploadedDocuments.forEach((doc, index) => {
        detailsText += `\nDocument ${index + 1}: ${doc.name}`;
        if (extractedContent[doc.id]) {
          detailsText += `\nExtracted Content:\n${extractedContent[doc.id]}\n`;
        }
      });
    }

    navigate("/drafts", {
      state: {
        query: detailsText,
        summary: detailsText,
        country: selectedCountry,
        state: selectedState,
        district: selectedDistrict,
        lawCategory,
        draftType: "legal-notice",
        entityName: formData.senderFullName,
        uploadedDocuments,
        extractedContent,
      },
    });
  };

  const renderStep1 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        👤 Sender Details
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Full Name *</label>
          <input
            type="text"
            name="senderFullName"
            value={formData.senderFullName}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Father's/Husband's Name *</label>
          <input
            type="text"
            name="senderFatherOrHusband"
            value={formData.senderFatherOrHusband}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Age *</label>
          <input
            type="number"
            name="senderAge"
            value={formData.senderAge}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Gender *</label>
          <select
            name="senderGender"
            value={formData.senderGender}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Occupation *</label>
          <input
            type="text"
            name="senderOccupation"
            value={formData.senderOccupation}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Contact Number *</label>
          <input
            type="tel"
            name="senderContact"
            value={formData.senderContact}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Email ID</label>
          <input
            type="email"
            name="senderEmail"
            value={formData.senderEmail}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-page-col">
          <label>Address *</label>
          <textarea
            name="senderAddress"
            value={formData.senderAddress}
            onChange={handleInputChange}
            rows={2}
            required
          />
        </div>
      </div>

      <LocationSelectors
        country={formData.country}
        state={formData.state}
        district={formData.district}
        onChange={(next) => setFormData((prev) => ({ ...prev, ...next }))}
      />
    </div>
  );

  const renderStep2 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        👥 Recipient & Notice Details
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Notice Type *</label>
          <select
            name="noticeType"
            value={formData.noticeType}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Notice Type</option>
            <option value="Breach of Contract">Breach of Contract</option>
            <option value="Payment Default">Payment Default</option>
            <option value="Property Dispute">Property Dispute</option>
            <option value="Employment Issue">Employment Issue</option>
            <option value="Defamation">Defamation</option>
            <option value="Copyright Infringement">Copyright Infringement</option>
            <option value="Trademark Violation">Trademark Violation</option>
            <option value="Matrimonial Dispute">Matrimonial Dispute</option>
            <option value="Consumer Protection">Consumer Protection</option>
            <option value="Other">Other (specify)</option>
          </select>
        </div>
        <div className="form-page-col">
          {formData.noticeType === "Other" && (
            <>
              <label>Specify Notice Type *</label>
              <input
                type="text"
                name="noticeTypeOther"
                value={formData.noticeTypeOther}
                onChange={handleInputChange}
                required
              />
            </>
          )}
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Recipient Name *</label>
          <input
            type="text"
            name="recipientName"
            value={formData.recipientName}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Designation</label>
          <input
            type="text"
            name="recipientDesignation"
            value={formData.recipientDesignation}
            onChange={handleInputChange}
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Organization/Company</label>
          <input
            type="text"
            name="recipientOrganization"
            value={formData.recipientOrganization}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-page-col">
          <label>Recipient Contact</label>
          <input
            type="tel"
            name="recipientContact"
            value={formData.recipientContact}
            onChange={handleInputChange}
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Recipient Email</label>
          <input
            type="email"
            name="recipientEmail"
            value={formData.recipientEmail}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-page-col">
          <label>Recipient Address *</label>
          <textarea
            name="recipientAddress"
            value={formData.recipientAddress}
            onChange={handleInputChange}
            rows={2}
            required
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        📋 Notice Content
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Notice Subject *</label>
          <input
            type="text"
            name="noticeSubject"
            value={formData.noticeSubject}
            onChange={handleInputChange}
            placeholder="Brief subject line for the legal notice"
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Legal Basis *</label>
          <textarea
            name="legalBasis"
            value={formData.legalBasis}
            onChange={handleInputChange}
            rows={3}
            placeholder="Legal grounds and basis for sending this notice..."
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Facts of the Case *</label>
          <textarea
            name="factsOfCase"
            value={formData.factsOfCase}
            onChange={handleInputChange}
            rows={4}
            placeholder="Chronological facts and circumstances leading to this notice..."
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Grievance Details *</label>
          <textarea
            name="grievanceDetails"
            value={formData.grievanceDetails}
            onChange={handleInputChange}
            rows={3}
            placeholder="Specific grievances and complaints against the recipient..."
            required
          />
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <>
      <div className="form-page-section">
        <div className="form-page-section-title">
          ⚖️ Legal Demands & Consequences
        </div>
        <div className="form-page-row">
          <div className="form-page-col full-width">
            <label>Laws Violated</label>
            <textarea
              name="lawsViolated"
              value={formData.lawsViolated}
              onChange={handleInputChange}
              rows={3}
              placeholder="Specific laws, sections, or regulations violated..."
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col full-width">
            <label>Demanded Action *</label>
            <textarea
              name="demandAction"
              value={formData.demandAction}
              onChange={handleInputChange}
              rows={3}
              placeholder="Specific actions demanded from the recipient..."
              required
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Time Limit for Response *</label>
            <select
              name="timeLimit"
              value={formData.timeLimit}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Time Limit</option>
              <option value="7 days">7 days</option>
              <option value="15 days">15 days</option>
              <option value="30 days">30 days</option>
              <option value="45 days">45 days</option>
              <option value="60 days">60 days</option>
              <option value="90 days">90 days</option>
            </select>
          </div>
          <div className="form-page-col">
            <label>Urgency Level</label>
            <select
              name="urgency"
              value={formData.urgency}
              onChange={handleInputChange}
            >
              <option value="">Select Urgency</option>
              <option value="Immediate">Immediate</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Normal">Normal</option>
            </select>
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col full-width">
            <label>Consequences of Non-Compliance *</label>
            <textarea
              name="consequences"
              value={formData.consequences}
              onChange={handleInputChange}
              rows={3}
              placeholder="Legal consequences if the demanded action is not taken..."
              required
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Evidence Documents</label>
            <textarea
              name="evidenceDocuments"
              value={formData.evidenceDocuments}
              onChange={handleInputChange}
              rows={2}
              placeholder="List of supporting documents attached..."
            />
          </div>
          <div className="form-page-col">
            <label>Previous Correspondence</label>
            <textarea
              name="previousCorrespondence"
              value={formData.previousCorrespondence}
              onChange={handleInputChange}
              rows={2}
              placeholder="Previous attempts to resolve the matter..."
            />
          </div>
        </div>
      </div>

      <div className="form-page-section">
        <div className="form-page-section-title">
          📄 Supporting Documents
        </div>
        <p className="form-page-note">
          Upload relevant documents such as contracts, agreements, correspondence, 
          receipts, or any other supporting materials for your legal notice. The system 
          will extract text from these documents to enhance your legal draft.
        </p>
        <DocumentUpload 
          onDocumentsChange={handleDocumentsChange}
          maxFiles={8}
          acceptedTypes=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
        />
      </div>

      <div className="form-page-declaration">
        <div className="form-page-section-title">
          ✅ Declaration
        </div>
        <label>
          <input
            type="checkbox"
            name="declaration"
            checked={formData.declaration}
            onChange={handleInputChange}
            required
          />
          <span>I declare that all the facts stated in this notice are true and correct *</span>
        </label>
      </div>
    </>
  );

  return (
    <div className="form-page-container">
      <div className="form-page-header">
        <a href="#" onClick={() => navigate(-1)} className="form-page-back-link">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Draft Selection
        </a>
        <h1 className="form-page-title">Create Legal Notice</h1>
        <p className="form-page-subtitle">Fill in the details below to generate a professional legal notice</p>
      </div>

      <div className="form-page-content">
        <form onSubmit={handleSubmit}>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}

          <div className="form-page-navigation">
            <div className="form-page-step-indicator">
              Step {currentStep} of {totalSteps}
            </div>
            <div className="form-page-buttons">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="form-page-btn form-page-btn-secondary"
                >
                  Previous
                </button>
              )}
              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="form-page-btn form-page-btn-primary"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  className="form-page-btn form-page-btn-primary"
                >
                  Generate Legal Notice
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LegalNoticeFormPage;
